<script setup lang="ts">
import Modal from '~common/modal.vue'
import BaseButton from '~common/base-button.vue'
import { useModal } from '~/composables/common/use-modal'
import { useApi } from '~/composables/common/use-api'
import { useMainBank } from '~/composables/account/use-main-bank'
import { toastPopup } from '~/utils/modal/toast'
import { USER_API } from '~/configs/api'
import { ModalCollection } from '~/constants'
import { HttpRequestMethod } from '~/configs/enums'
import { ResponseStatus } from '~composables/common/interface'

const { data, bank } = defineProps<{
  data: { bank_code: string, bank_account_name: string, bank_account_no: string }
  bank: { name: string; image: string}
}>()

const { isMobile } = useDevice()
const { closeModal, closeAllModal } = useModal()
const { request } = useApi()
const { fetchUserMainBanks } = useMainBank()

const confirm = async () => {
  try {
    const { status, message } = await request(
      USER_API.ADD_BANK_ACCOUNT,
      { method: HttpRequestMethod.POST, body: data }
    )
    if (status === ResponseStatus.OK && message) {
      fetchUserMainBanks()
      toastPopup({ title: 'Đã thêm tài khoản ngân hàng', type: 'success', isMobile })
      closeAllModal()
      return
    } else {
      toastPopup({ title: 'Lỗi mạng, vui lòng thử lại', type: 'error', isMobile })
    }
  } catch {
    toastPopup({ title: 'Lỗi mạng, vui lòng thử lại', type: 'error', isMobile })
  }
}
</script>

<template>
  <Modal
    :modalId="ModalCollection.CONFIRM_ADD_BANK_ACCOUNT"
    modalDialogClasses="modal-auth__dialog width-480 add-bank-account-dialog"
    modalClass="wrapper-modal-custom"

  >
    <div class="confirm-account-modal">
      <div class="confirm-account-modal__content">
        <NuxtImg src="/assets/images/popup/user-confirm.webp" alt="Confirm icon" width="88" />
        <div class="confirm-account-modal__title">Xác nhận thông tin</div>

        <div class="w-100">
          <div class="confirm-account-modal__data">
            <div class="confirm-account-modal__data__label">Ngân hàng</div>
            <div class="confirm-account-modal__data__value">
              <div class="confirm-account-modal__data__bank-icon">
                <img :src="bank.image" :alt="bank.name" width="16" />
              </div>
              {{ bank.name }}
            </div>
          </div>
          <div class="confirm-account-modal__data">
            <div class="confirm-account-modal__data__label">Tên chủ tài khoản</div>
            <div class="confirm-account-modal__data__value">{{ data.bank_account_name }}</div>
          </div>
          <div class="confirm-account-modal__data">
            <div class="confirm-account-modal__data__label">Số tài khoản</div>
            <div class="confirm-account-modal__data__value">{{ data.bank_account_no }}</div>
          </div>
        </div>
      </div>

      <div class="confirm-account-modal__note">
        Để tránh ảnh hưởng đến quá trình Nạp - Rút tiền, vui lòng kiểm tra lại thông tin tài khoản trước khi nhấn
        "Xác nhận".
      </div>

      <div class="confirm-account-modal__buttons">
        <BaseButton class="base-button__outlined" @click="closeModal(ModalCollection.CONFIRM_ADD_BANK_ACCOUNT)">
          Huỷ
        </BaseButton>
        <BaseButton @click="confirm">Xác nhận</BaseButton>
      </div>
    </div>
  </Modal>
</template>

<style scoped lang="scss">
.confirm-account-modal {
  display: flex;
  flex-direction: column;
  gap: 24px;

  &__content {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
  }

  &__title {
    font-size: 24px;
    font-weight: 700;
    line-height: 32px;
    color: $neutral-900;
  }

  &__data {
    display: flex;
    justify-content: space-between;
    line-height: 24px;

    &:not(:last-child) {
      padding-bottom: 8px;
      border-bottom: 1px solid $neutral-200;
    }

    &:not(:first-child) {
      padding-top: 8px;
    }

    &__bank-icon {
      display: inline-block;
      padding: 0 4px;
      border-radius: 50%;
      background-color: $neutral-100;
    }

    &__label {
      line-height: 24px;
      color: $neutral-600;
    }

    &__value {
      font-weight: 600;
      text-transform: uppercase;
      color: $neutral-800;
    }
  }

  &__note {
    padding: 8px;
    border-radius: 4px;
    font-size: 12px;
    line-height: 16px;
    color: $blue-800;
    background-color: $blue-200;
  }

  &__buttons {
    display: flex;
    gap: 16px;
    width: 100%;
  }
}
</style>
